// src/Pages/Split/splitData.js
// ─────────────────────────────────────────────────────────────────────────
// Blitz Split — data layer: row factory, email→name generation, CSV
// import (email + amount only), and the contractor-preset store
// (localStorage now, backend later).
// ─────────────────────────────────────────────────────────────────────────

import client from "../../../API";
import { isEmailish } from "./SplitTokens";

// Re-export so data consumers can import everything row-related from here
export { isEmailish };

// ── Row factory ───────────────────────────────────────────────────────────
let rowSeq = 0;
export const makeRow = (kind, extra = {}) => ({
  rowId: ++rowSeq,
  kind,                 // 'me' | 'creator' | 'invite'
  creator: null,
  name: "",
  email: "",
  amount: "",
  blitzpayPre: false,   // early payout pre-accepted on their slice
  presetApplied: false, // row was auto-filled from a saved preset
  ...extra,
});

// ── Display name from email: jane.smith@x.com → "Jane4827" ───────────────
export const nameFromEmail = (email) => {
  const local = String(email || "").split("@")[0] || "user";
  const first = local.split(/[._\-+]/)[0] || "user";
  const pretty = first.charAt(0).toUpperCase() + first.slice(1).toLowerCase();
  const rand = Math.floor(1000 + Math.random() * 9000);
  return `${pretty}${rand}`;
};

// ── Preset keys: stable creator id, or lowercased email for invitees ─────
export const presetKeyFor = (row) =>
  row.kind === "creator" ? `c:${row.creator?.creator}` :
  row.kind === "invite" && row.email ? `e:${row.email.trim().toLowerCase()}` : null;

export const presetKeyForCreator = (c) => `c:${c.creator}`;
export const presetKeyForEmail = (email) => `e:${String(email || "").trim().toLowerCase()}`;

// ── Preset store — localStorage now, backend later ────────────────────────
const PRESET_LS_KEY = "blitz_split_presets_v1";

export function loadLocalPresets() {
  try { return JSON.parse(localStorage.getItem(PRESET_LS_KEY) || "{}"); }
  catch { return {}; }
}

export function persistLocalPresets(presets) {
  try { localStorage.setItem(PRESET_LS_KEY, JSON.stringify(presets)); }
  catch { /* quota — ignore */ }
}

export async function fetchServerPresets() {
  try {
    if (client.splitPresets?.list) {
      const remote = await client.splitPresets.list();
      if (remote && typeof remote === "object") return remote;
    }
  } catch { /* endpoint not live yet */ }
  return null;
}

export function syncPresetToServer(key, preset) {
  try {
    if (preset) client.splitPresets?.save?.({ key, ...preset });
    else client.splitPresets?.remove?.({ key });
  } catch { /* best-effort */ }
}

// ── CSV: email + amount ONLY (handles quoted fields) ──────────────────────
// No name column is read. Rows must carry a valid email; the amount column
// is optional per-row (presets or manual entry fill the gaps).
export function parseCSV(text) {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (!lines.length) return [];

  const splitLine = (line) => {
    const out = []; let cur = "", inQ = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQ && line[i + 1] === '"') { cur += '"'; i++; }
        else inQ = !inQ;
      } else if (ch === "," && !inQ) { out.push(cur.trim()); cur = ""; }
      else cur += ch;
    }
    out.push(cur.trim());
    return out;
  };

  let rows = lines.map(splitLine);

  // Default positional layout: email, amount
  let idx = { email: 0, amount: 1 };
  const head = rows[0].map((c) => c.toLowerCase());
  const hasHeader = head.some((c) =>
    ["email", "e-mail", "amount", "total", "share", "payout"].includes(c));
  if (hasHeader) {
    const find = (...keys) => head.findIndex((c) => keys.includes(c));
    idx = {
      email:  Math.max(find("email", "e-mail"), 0),
      amount: Math.max(find("amount", "total", "share", "payout"), 1),
    };
    rows = rows.slice(1);
  }

  return rows
    .map((cols) => ({
      email:  cols[idx.email]  || "",
      amount: (cols[idx.amount] || "").replace(/[$,\s]/g, ""),
    }))
    .filter((r) => isEmailish(r.email));   // email is the identity — no email, no row
}